import { useRef, useState, useCallback, useEffect } from 'react';
import { scrollToPosition } from 'utils/scroll-to-position';
import { DEFAULT_CARD_WIDTH } from 'common/constants';

type UseCarouselLogicProps = {
  canScrollCallBack?: (left?: boolean, right?: boolean) => void;
  scrollStep?: number;
  onChange?: (value: number) => void;
};

export const CHECK_CAN_SCROLL_TIMEOUT = 500;
export const indentation = 150;

export const useCarouselLogic = ({ scrollStep, onChange, canScrollCallBack }: UseCarouselLogicProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [inScrollNow, setInScrollNow] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const onControlClick = useCallback(
    (direction: 'left' | 'right') => {
      const wrapper = containerRef.current;

      if (wrapper) {
        const { scrollLeft, offsetWidth, firstElementChild } = wrapper;
        const cardWidth = firstElementChild?.clientWidth ?? DEFAULT_CARD_WIDTH;
        const fitsInViewportCount = Math.floor(offsetWidth / cardWidth);
        let itemMargins = 0;
        if (firstElementChild) {
          const { gap, columnGap } = getComputedStyle(wrapper);
          const { marginLeft, marginRight } = getComputedStyle(firstElementChild);

          itemMargins =
            parseFloat(gap) || parseFloat(columnGap) || parseFloat(marginLeft) + parseFloat(marginRight);
        }

        const evenScrollWidth = (cardWidth + itemMargins) * fitsInViewportCount;
        const step = scrollStep ?? evenScrollWidth;

        setInScrollNow(true);

        let offset = 0;
        if (direction === 'left') {
          offset = scrollLeft - step;
        }
        if (direction === 'right') {
          offset = scrollLeft + step;
        }

        if (onChange) onChange(offset);

        scrollToPosition(
          { x: offset, y: 0 },
          { elementToScroll: wrapper, maxDuration: 650, easingFn: 'easeInOutCubic' },
        ).then(() => setInScrollNow(false));
      }
    },
    [onChange, scrollStep],
  );

  const onScroll = ({ target }: Event) => {
    const { scrollLeft } = target as HTMLDivElement;
    if (!inScrollNow && onChange) onChange(scrollLeft);
    checkCanScroll();
  };

  const checkCanScroll = useCallback(() => {
    const element = containerRef.current;
    if (element) {
      const { scrollLeft, offsetWidth, scrollWidth } = element;
      /**
       * Carousel can scroll to left
       * Hide or show left arrow
       */
      if (scrollLeft < indentation) {
        setCanScrollLeft(false);
      } else {
        setCanScrollLeft(true);
      }
      /**
       * Carousel can scroll to right
       * Hide or show right arrow
       */
      if (scrollWidth - offsetWidth - indentation < scrollLeft) {
        setCanScrollRight(false);
      } else {
        setCanScrollRight(true);
      }
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      checkCanScroll();
    }, CHECK_CAN_SCROLL_TIMEOUT);

    return () => clearTimeout(timer);
  }, [checkCanScroll]);

  useEffect(() => {
    if (canScrollCallBack) {
      canScrollCallBack(canScrollLeft, canScrollRight);
    }
  }, [canScrollCallBack, canScrollLeft, canScrollRight]);

  return {
    containerRef,
    inScrollNow,
    onControlClick,
    onScroll,
    canScrollLeft,
    canScrollRight,
  };
};
